import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, XCircleIcon, MapPinIcon, BanknoteIcon, CalendarIcon } from 'lucide-react';
import { useStore } from '../context/StoreContext';
import { OrderTracker } from '../components/OrderTracker';
export function OrderTrackingPage() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const {
    orders,
    cancelOrder,
    user
  } = useStore();
  if (!user) {
    navigate('/login');
    return null;
  }
  const order = orders.find(o => o.id === id);
  if (!order) {
    return <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-4 py-20" data-id="element-1179">
        <h2 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-1180">
          Order Not Found
        </h2>
        <p className="text-slate-500 mb-6" data-id="element-1181">
          We couldn't find the order you're looking for.
        </p>
        <Link to="/my-orders" className="px-6 py-3 bg-sky-600 text-white font-medium rounded-lg hover:bg-sky-700 transition-colors" data-id="element-1182">
          View All Orders
        </Link>
      </div>;
  }
  const canCancel = order.status === 'Placed' || order.status === 'Confirmed';
  return <div className="min-h-screen bg-slate-50" data-id="element-1183">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8" data-id="element-1184">
        <div className="flex items-center gap-4 mb-6" data-id="element-1185">
          <button onClick={() => navigate('/my-orders')} className="p-2 text-slate-500 hover:text-sky-600 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-1186">
            <ArrowLeftIcon className="w-5 h-5" data-id="element-1187" />
          </button>
          <div data-id="element-1188">
            <h1 className="text-xl md:text-2xl font-bold text-slate-900" data-id="element-1189">
              Order Details
            </h1>
            <p className="text-sm text-slate-500 font-mono" data-id="element-1190">{order.id}</p>
          </div>
        </div>

        <div className="space-y-6" data-id="element-1191">
          {/* Order Tracker */}
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="bg-white rounded-xl border border-slate-200 p-6" data-id="element-1192">
            <div className="flex items-center justify-between mb-6" data-id="element-1193">
              <h2 className="font-bold text-slate-900" data-id="element-1194">Order Status</h2>
              {order.deliveryDate && order.status !== 'Cancelled' && order.status !== 'Delivered' && <div className="flex items-center gap-2 bg-sky-50 text-sky-700 px-3 py-1.5 rounded-lg text-sm font-medium" data-id="element-1195">
                    <CalendarIcon className="w-4 h-4" data-id="element-1196" />
                    Expected:{' '}
                    {new Date(order.deliveryDate).toLocaleDateString('en-IN', {
                day: 'numeric',
                month: 'short'
              })}
                  </div>}
            </div>
            <OrderTracker status={order.status} deliveryDate={order.deliveryDate} data-id="element-1197" />
            {order.deliveryDate && order.status !== 'Cancelled' && <p className="text-sm text-slate-500 mt-4 text-center" data-id="element-1198">
                Estimated delivery:{' '}
                {new Date(order.deliveryDate).toLocaleDateString('en-IN', {
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            })}
              </p>}
          </motion.div>

          {/* Items */}
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="bg-white rounded-xl border border-slate-200 p-6" data-id="element-1199">
            <h2 className="font-bold text-slate-900 mb-4" data-id="element-1200">
              Items ({order.items.length})
            </h2>
            <div className="space-y-4" data-id="element-1201">
              {order.items.map(item => <div key={`${item.product.id}-${item.size}`} className="flex gap-4" data-id="element-1202">
                  <img src={item.product.images[0]} alt={item.product.name} className="w-16 h-20 object-cover rounded-lg bg-slate-100" data-id="element-1203" />
                  <div className="flex-1" data-id="element-1204">
                    <h3 className="font-medium text-slate-900 text-sm" data-id="element-1205">
                      {item.product.name}
                    </h3>
                    <p className="text-xs text-slate-500 mt-1" data-id="element-1206">
                      Size: {item.size} | Qty: {item.quantity}
                    </p>
                    <p className="text-sm font-bold text-slate-900 mt-1" data-id="element-1207">
                      ₹{item.product.sellingPrice * item.quantity}
                    </p>
                  </div>
                </div>)}
            </div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1208">
            {/* Delivery Address */}
            <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="bg-white rounded-xl border border-slate-200 p-6" data-id="element-1209">
              <div className="flex items-center gap-2 mb-3" data-id="element-1210">
                <MapPinIcon className="w-4 h-4 text-sky-600" data-id="element-1211" />
                <h2 className="font-bold text-slate-900" data-id="element-1212">Delivery Address</h2>
              </div>
              <div className="text-sm text-slate-600 space-y-1" data-id="element-1213">
                <p className="font-medium text-slate-900" data-id="element-1214">
                  {order.address.name}
                </p>
                <p data-id="element-1215">{order.address.address}</p>
                <p data-id="element-1216">
                  {order.address.city}, {order.address.state} -{' '}
                  {order.address.pincode}
                </p>
                <p data-id="element-1217">Phone: {order.address.phone}</p>
              </div>
            </motion.div>

            {/* Payment & Summary */}
            <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} className="bg-white rounded-xl border border-slate-200 p-6" data-id="element-1218">
              <div className="flex items-center gap-2 mb-3" data-id="element-1219">
                <BanknoteIcon className="w-4 h-4 text-sky-600" data-id="element-1220" />
                <h2 className="font-bold text-slate-900" data-id="element-1221">Payment</h2>
              </div>
              <div className="text-sm space-y-2" data-id="element-1222">
                <div className="flex justify-between text-slate-600" data-id="element-1223">
                  <span data-id="element-1224">Method</span>
                  <span className="font-medium text-slate-900" data-id="element-1225">
                    {order.paymentMethod === 'UPI' ? 'UPI Payment' : 'Cash on Delivery'}
                  </span>
                </div>
                <div className="flex justify-between text-slate-600" data-id="element-1226">
                  <span data-id="element-1227">Order Date</span>
                  <span data-id="element-1228">
                    {new Date(order.date).toLocaleDateString('en-IN')}
                  </span>
                </div>
                <div className="flex justify-between font-bold text-slate-900 pt-2 border-t border-slate-100" data-id="element-1229">
                  <span data-id="element-1230">Total</span>
                  <span data-id="element-1231">₹{order.totalAmount}</span>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Cancel Button */}
          {canCancel && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.4
        }} className="text-center" data-id="element-1232">
              <button onClick={() => {
            if (confirm('Are you sure you want to cancel this order?')) {
              cancelOrder(order.id);
            }
          }} className="inline-flex items-center gap-2 px-6 py-3 text-red-600 font-medium border-2 border-red-200 rounded-xl hover:bg-red-50 transition-colors" data-id="element-1233">
                <XCircleIcon className="w-5 h-5" data-id="element-1234" /> Cancel Order
              </button>
            </motion.div>}
        </div>
      </div>
    </div>;
}